import CloseIcon from "@mui/icons-material/Close";
import {useEffect, useMemo, useState} from "react";
import {getInvoiceOrderDetail, getInvoiceRecords, InvoiceRecordItem} from "../../api/payment";
import {useDispatch} from "react-redux";
import {setGlobalDialogTips} from "../../store/memberSlice";
import './styless.scss'
import {getLocalStorage} from "../../util/storage";
import {useMatomoTrackingPoint} from "../../hooks/useMatomoTrackingPoint";
import {QuestionIcon} from "../../icons";
import {Tooltip} from "@mui/material";

export default function Bill(props: { onClose?: () => void }) {
    const { onClose } = props;
    const publicUrl = process.env.PUBLIC_URL || '';
    const dispatch = useDispatch();
    const { trackInvoiceApplyClick, trackInvoiceApplyHoverShow } = useMatomoTrackingPoint();
    const [records, setRecords] = useState<InvoiceRecordItem[]>([]);
    const [loading, setLoading] = useState(false);

    const refreshBillRecords = () => {
        setLoading(true);
        getInvoiceRecords(1, 999)
            .then((data) => {
                setRecords(data?.records || []);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        refreshBillRecords();
    }, []);

    const formatTime = (time: string) => {
        if (!time) return '--';
        const date = new Date(time);
        if (Number.isNaN(date.getTime())) return time;
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const mi = String(date.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    };

    const statusColorMap: Record<string, string> = useMemo(() => ({
        completed: 'var(--c-income-strong)',
        rejected: 'var(--c-danger)',
        processing: 'var(--c-warning)',
        available: 'var(--c-text-primary)',
        vailable: 'var(--c-text-primary)',
        unavailable: 'var(--c-text-secondary)',
    }), []);

    const getStatusText = (item: InvoiceRecordItem) => {
        switch (item.invoice_status) {
            case 'completed':
                return '已开票';
            case 'rejected':
                return <span className="bill-status-row">
                    已驳回
                    <Tooltip
                        title={item.reject_reason || '已驳回'}
                        placement="top"
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                    >
                        <span className="bill-tooltip-wrapper">
                            <QuestionIcon/>
                        </span>
                    </Tooltip>
                </span>;
            case 'processing': {
                const applyDate = item.apply_time
                    ? (() => {
                        const d = new Date(item.apply_time!);
                        const yyyy = d.getFullYear();
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        return `${yyyy}-${mm}-${dd}`;
                    })()
                    : '--';
                const deadline = item.apply_time
                    ? (() => {
                        const d = new Date(item.apply_time!);
                        d.setDate(d.getDate() + 14);
                        const yyyy = d.getFullYear();
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        return `${yyyy}-${mm}-${dd}`;
                    })()
                    : '--';
                return <span className="bill-status-row">
                    审核中
                    <Tooltip
                        title={<div className="bill-tooltip-title"><div>申请日期: {applyDate}</div><div>预计在 {deadline} 前办理开票</div></div>}
                        placement="top"
                        onOpen={() => trackInvoiceApplyHoverShow({ page_id: window.location.href })}
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                    >
                        <span className="bill-tooltip-wrapper">
                            <QuestionIcon/>
                        </span>
                    </Tooltip>
                </span>;
            }
            case 'available':
                return '未开票';
            case 'unavailable':
                return '--';
            default:
                return '--';
        }
    };

    const formatPaymentDescription = (description: string) => {
        const planMap: Record<string, string> = {
            plus: '月度Plus会员',
            pro: '月度Pro会员',
            max: '月度Max会员',
            plus_year: '年度Plus会员',
            pro_year: '年度Pro会员',
            max_year: '年度Max会员',
        };

        return planMap[description] || description || '--';
    };

    const handleApplyInvoiceClick = async (item: InvoiceRecordItem) => {
        try {
            const detail = await getInvoiceOrderDetail(item.out_trade_no);
            const status = detail?.invoice_status;
            if (status && status !== 'available' && status !== 'rejected') {
                dispatch(setGlobalDialogTips({
                    type: 'error',
                    title: '申请失败',
                    content: '该账单已申请开具发票，无法重复申请',
                    buttonContent: '确认',
                    confirmFun: () => refreshBillRecords(),
                }));
                return;
            }

            window.open(`${window.location.origin}${publicUrl}/invoice/${item.out_trade_no}`, '_blank');
        } catch (e) {
            dispatch(setGlobalDialogTips({
                type: 'error',
                title: '申请失败',
                content: '获取发票详情失败，请稍后重试',
                buttonContent: '确认',
                confirmFun: () => refreshBillRecords(),
            }));
        }
    };

    const handleDownloadInvoiceClick = async (item: InvoiceRecordItem) => {
        try {
            const userInfoStr = getLocalStorage('userInfo');
            const localUuid = userInfoStr ? (JSON.parse(userInfoStr)?.uuid as string | undefined) : undefined;
            const uuid = localUuid;
            const pdfFilename = item?.pdf_url;

            if (!uuid || !pdfFilename) {
                dispatch(setGlobalDialogTips({
                    type: 'error',
                    title: '下载失败',
                    content: '发票文件不存在或未生成，请稍后再试',
                    buttonContent: '确认',
                    confirmFun: () => refreshBillRecords(),
                }));
                return;
            }

            window.open(pdfFilename, '_blank');
        } catch (e) {
            dispatch(setGlobalDialogTips({
                type: 'error',
                title: '下载失败',
                content: '获取发票文件失败，请稍后重试',
                buttonContent: '确认',
                confirmFun: () => refreshBillRecords(),
            }));
        }
    };

    const handleButton = (item: InvoiceRecordItem) => {
        if (item.invoice_status === 'available' || item.invoice_status === 'rejected') {
            return (
                <div
                    className='bill-btn-action'
                    onClick={() => {
                        trackInvoiceApplyClick({
                            page_id: window.location.href,
                            switch_status: '开具发票',
                        });
                        handleApplyInvoiceClick(item);
                    }}
                >
                    开具发票
                </div>
            );
        }

        if (item.invoice_status === 'processing') {
            return (
                <div
                    className='bill-btn-action-disabled'
                >
                    开具发票
                </div>
            );
        }

        if (item.invoice_status === 'completed') {
            return (
                <div
                    className='bill-btn-action'
                    onClick={() => {
                        trackInvoiceApplyClick({
                            page_id: window.location.href,
                            switch_status: '下载发票',
                        });
                        handleDownloadInvoiceClick(item)
                    }}
                >
                    下载发票
                </div>
            );
        }

        return <div className='bill-btn-default'>--</div>;
    };

    const labelStatus = (item: InvoiceRecordItem) => {
        const color = statusColorMap[item.invoice_status] || 'var(--c-text-primary)';
        return <div className='bill-label-status' style={{color}}>{getStatusText(item)}</div>;
    };

    const renderMobileButton = (item: InvoiceRecordItem) => {
        const isActive = item.invoice_status === 'available' || item.invoice_status === 'rejected';
        const isCompleted = item.invoice_status === 'completed';

        if (isActive) {
            return (
                <div
                    className="mobile-bill-btn"
                    onClick={() => {
                        trackInvoiceApplyClick({
                            page_id: window.location.href,
                            switch_status: '开具发票',
                        });
                        handleApplyInvoiceClick(item);
                    }}
                >
                    开具发票
                </div>
            );
        }

        if (item.invoice_status === 'processing') {
            return (
                <div className="mobile-bill-btn mobile-bill-btn--disabled">
                    开具发票
                </div>
            );
        }

        if (isCompleted) {
            return (
                <div
                    className="mobile-bill-btn"
                    onClick={() => {
                        trackInvoiceApplyClick({
                            page_id: window.location.href,
                            switch_status: '下载发票',
                        });
                        handleDownloadInvoiceClick(item);
                    }}
                >
                    下载发票
                </div>
            );
        }

        return <div></div>;
    };

    return <div className='bill-container'>
        <div className='title'>
            <span>账单记录</span><span
            onClick={() => {
                onClose?.();
            }}><CloseIcon/></span></div>
        <div className='dropWaterDetailLayout bill-layout-wrap pc-only'>
            <div className='dropWaterDetailMain bill-main'>
                <div className='dropWaterDetailTableHeader bill-table-header'>
                    <div className='dropWaterDetailCol200'>消费时间</div>
                    <div className='dropWaterDetailCol130'>消费金额</div>
                    <div className='dropWaterDetailCol200'>消费项目</div>
                    <div className='dropWaterDetailCol70'>状态</div>
                    <div className='dropWaterDetailCol80'>操作</div>
                </div>
                <div className="bill-table-body">
                    {loading && <div className='bill-empty-text'>加载中...</div>}
                    {!loading && records.length === 0 && <div className='bill-empty-text'>暂无账单记录</div>}
                    {!loading && records.map((item) => (
                        <div key={item.out_trade_no} className='dropWaterDetailTableRow bill-table-row'>
                            <div className='dropWaterDetailCol200 bill-col-cell'>{formatTime(item.payment_time)}</div>
                            <div className='dropWaterDetailCol130 bill-col-cell'>¥{item.payment_amount}</div>
                            <div className='dropWaterDetailCol200 bill-col-cell'>{formatPaymentDescription(item.consume_description)}</div>
                            <div className='dropWaterDetailCol70Red bill-col-cell'>
                                {labelStatus(item)}
                            </div>
                            <div className='dropWaterDetailCol70Red bill-col-action'>
                                {handleButton(item)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className='bill-contact'>遇到问题？ <span className='bill-contact-link'  onClick={() => window.open('https://qm.qq.com/q/DdeNxVg01i', '_blank')}>联系客服</span></div>
        </div>
        <div className='mobile-only dropWaterDetailLayoutForMobile'>
            {loading && <div className='bill-empty-text'>加载中...</div>}
            {!loading && records.length === 0 && <div className='bill-empty-text'>暂无账单记录</div>}
            {!loading && records.map((item) => (
                <div key={item.out_trade_no} className="mobile-bill-card">
                    <div className="mobile-bill-left">
                        <div className="mobile-bill-title">{formatPaymentDescription(item.consume_description)}</div>
                        <div className="mobile-bill-time">{formatTime(item.payment_time)}</div>
                        <div className="mobile-bill-status" style={{color: statusColorMap[item.invoice_status] || 'var(--c-text-primary)'}}>
                            {getStatusText(item)}
                        </div>
                    </div>
                    <div className="mobile-bill-right">
                        <div className="mobile-bill-price">¥{item.payment_amount}</div>
                        {renderMobileButton(item)}
                    </div>
                </div>
            ))}
        </div>
    </div>
}